import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LegalModals } from './components/LegalModals';
import { CookieBanner } from './components/CookieBanner';
import { Admin } from './components/Admin';
import { NotFound } from './components/NotFound';
import { Contact } from './components/Contact';

// Pages
import { Home } from './pages/Home';
import { Services } from './pages/Services';
import { WhyUs } from './pages/WhyUs';
import { CasesPage } from './pages/CasesPage';
import { ProcessPage } from './pages/ProcessPage';
import { TeamPage } from './pages/TeamPage';
import { Linktree } from './components/Linktree';
import { PageHeader } from './components/PageHeader';

// Error Boundary Component
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white p-8">
          <div className="max-w-2xl w-full bg-red-50 p-8 rounded-2xl border border-red-100">
            <h1 className="text-2xl font-black text-red-600 mb-4 uppercase">Systemfehler</h1>
            <p className="font-bold text-red-800 mb-4">Ein unerwarteter Fehler ist aufgetreten.</p>
            <pre className="bg-white p-4 rounded-xl text-xs font-mono overflow-auto border border-red-100 text-red-900 max-h-64">
              {this.state.error?.toString()}
            </pre>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-6 px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition"
            >
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const App: React.FC = () => {
  const [legalModalType, setLegalModalType] = useState<'impressum' | 'privacy' | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);

  // Helper to get current path and normalize it
  const getPath = () => {
    let path = window.location.pathname;
    // Remove trailing slash if it exists and path is not root
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    // Convert to lowercase to ensure case-insensitive matching
    return path.toLowerCase();
  };

  const [currentPath, setCurrentPath] = useState(getPath());

  // History Router Logic
  useEffect(() => {
    const handleRouteChange = () => {
      const path = getPath();
      setCurrentPath(path);
      window.scrollTo(0, 0);
    };
    
    // Listen to popstate (browser back/forward) AND our custom event
    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('route-change', handleRouteChange);
    
    // Initial check
    handleRouteChange();

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('route-change', handleRouteChange);
    };
  }, []);

  // Analytics Tracking
  useEffect(() => {
    if (showAdmin) return;

    const trackVisit = async () => {
      // Basic tracking payload
      const payload = {
        path: currentPath,
        referrer: document.referrer,
        userAgent: navigator.userAgent
      };

      try {
        await fetch('/api/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (e) {
        // Silent fail for analytics
      }
    };

    trackVisit();
  }, [currentPath, showAdmin]);

  // Admin Interface
  if (showAdmin) {
    return (
      <ErrorBoundary>
        <Admin onClose={() => setShowAdmin(false)} />
      </ErrorBoundary>
    );
  }

  const renderContent = () => {
      // Handle Scroll-To-Anchor style routes
      if (currentPath === '/kontakt') {
          return (
            <>
              <PageHeader title="Let's//Talk." subtitle="Kontakt" />
              <Contact />
            </>
          );
      }

      switch (currentPath) {
          case '/':
              return <Home />;
          case '/leistungen':
              return <Services />;
          case '/warum-wir':
              return <WhyUs />;
          case '/cases':
              return <CasesPage />;
          case '/prozess':
              return <ProcessPage />;
          case '/team':
              return <TeamPage />;
          
          // Linktree / Visitenkarten Routes
          case '/linktree':
              return <Linktree />;
          case '/edin':
              return <Linktree profileId="edin" />;
          case '/arne':
              return <Linktree profileId="arne" />;
          case '/hakan':
              return <Linktree profileId="hakan" />;
              
          default:
              return <NotFound />;
      }
  };

  // Check if we are in a standalone/fullscreen linktree mode to hide Nav/Footer
  const isLinktreeRoute = ['/edin', '/arne', '/hakan'].includes(currentPath);

  return (
    <ErrorBoundary>
      <div className="font-sans text-brand-dark antialiased bg-brand-gray selection:bg-brand-orange selection:text-white">
        {!isLinktreeRoute && <Navbar currentPath={currentPath} />}
        
        <main className="min-h-screen">
          {renderContent()}
        </main>
        
        {!isLinktreeRoute && (
          <Footer 
            onOpenImpressum={() => setLegalModalType('impressum')}
            onOpenPrivacy={() => setLegalModalType('privacy')}
            onOpenAdmin={() => setShowAdmin(true)}
          />
        )}
        
        <LegalModals 
          isOpen={!!legalModalType} 
          type={legalModalType} 
          onClose={() => setLegalModalType(null)} 
        />
        <CookieBanner />
      </div>
    </ErrorBoundary>
  );
};

export default App;